import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal, AdminAccountActionModal } from '../components/common';
import { ROUTES } from '../constants';
import { shopperService, marketerService, ApiError } from '../services';

const isZeroDate = value =>
  !value || String(value).startsWith('0001-01-01');

const ShopperDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const stateShopper = location.state?.shopper || null;
  const backToUpline = location.state?.backToUpline || null;
  const breadcrumbsOverride = location.state?.breadcrumbsOverride || null;

  const [shopper, setShopper] = useState(stateShopper);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailFetched, setDetailFetched] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [actionModal, setActionModal] = useState({ isOpen: false, type: 'delete' });
  const [actionSuccess, setActionSuccess] = useState(null);
  // Local visual hide on top of BE-raw data while elevation is still active.
  // Toggling this does NOT call the API; BE remains revealed until TTL ends.
  // Refreshing or letting elevation expire returns to the BE-masked flow.
  const [localHidden, setLocalHidden] = useState(false);

  // `revealed` is only authoritative AFTER a fresh detail fetch — list
  // endpoint doesn't include it, so `location.state.shopper` would otherwise
  // render the toggle prematurely (Show button flash on refresh).
  const revealed = shopper?.revealed === true;
  // Field is effectively masked when: BE masked it, OR user toggled local hide
  // while elevated. In the first case BE already returns mask strings; in the
  // second case FE applies maskers to the raw values.
  const effectivelyMasked = !revealed || localHidden;

  const [downline, setDownline] = useState([]);
  const [downlineLoading, setDownlineLoading] = useState(false);
  const [downlineError, setDownlineError] = useState(null);
  const [downlinePagination, setDownlinePagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    setShopper(stateShopper || null);
    setDetailFetched(false);
    setLocalHidden(false);
  }, [id, stateShopper]);

  const fetchShopperDetails = useCallback(async () => {
    if (!id) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await shopperService.getFormattedShopperDetails(id);
      if (response && response.data) {
        setShopper(response.data);
        setDetailFetched(true);
      }
    } catch (err) {
      console.error('Failed to fetch shopper details:', err);
      if (err instanceof ApiError) {
        setDetailError(`Failed to load shopper details: ${err.message}`);
      } else {
        setDetailError('Failed to load shopper details. Please try again.');
      }
    } finally {
      setDetailLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchShopperDetails();
  }, [fetchShopperDetails]);

  const fetchShopperDownline = useCallback(
    async (page = 1) => {
      if (!shopper?.accountId) return;

      setDownlineLoading(true);
      setDownlineError(null);

      try {
        const response = await marketerService.getFormattedShopperDownline(
          shopper.accountId,
          {
            page,
            size: downlinePagination.size,
            order: 'created_at DESC',
            from: 'S',
          }
        );

        if (response && response.data) {
          setDownline(response.data.data || []);
          setDownlinePagination({
            page: response.data.page || 1,
            pageCount: response.data.pageCount || 1,
            size: response.data.size || 20,
            count: response.data.count || 0,
            total: response.data.total || 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch shopper downline:', err);
        setDownline([]);
        if (err instanceof ApiError) {
          setDownlineError(`Failed to load downline: ${err.message}`);
        } else {
          setDownlineError('Failed to load downline. Please try again.');
        }
      } finally {
        setDownlineLoading(false);
      }
    },
    [downlinePagination.size, shopper?.accountId]
  );

  useEffect(() => {
    if (!shopper?.accountId) return;
    fetchShopperDownline(1);
  }, [fetchShopperDownline, shopper?.accountId]);

  const handleDownlinePageChange = useCallback(
    page => {
      if (page < 1 || page > downlinePagination.pageCount) return;
      fetchShopperDownline(page);
    },
    [downlinePagination.pageCount, fetchShopperDownline]
  );

  const display = value => {
    if (value === null || value === undefined) return '-';
    const text = String(value).trim();
    return text && text !== 'N/A' ? text : '-';
  };

  // FE masking, applied only when revealed=true and user toggled local hide.
  // When revealed=false, BE already returns mask strings; we render as-is.
  const maskTail = (value, keep = 4) => {
    if (!value) return '-';
    const str = String(value);
    if (str.length <= keep) return '*'.repeat(Math.max(2, str.length));
    return `**${str.slice(-keep)}`;
  };
  const maskPhone = value => {
    if (!value) return '-';
    const str = String(value);
    const prefix = str.startsWith('+') ? '+' : '';
    return `${prefix}**${str.slice(-4)}`;
  };
  const maskFull = value => (value ? '******' : '-');
  // Pre-fetch we render a blank placeholder so list-state values never leak
  // raw. Zero-width space stays truthy and survives `.trim()` in `display()`,
  // so visually it renders empty without triggering the '-' fallback.
  const sensitiveValue = (value, masker = maskFull) => {
    if (!detailFetched) return '​';
    return revealed && localHidden ? masker(value) : value;
  };

  // Birthdate needs FE-side handling: BE returns zero-time when masked (DTO
  // can't be nullable). Show blank pre-fetch, ****** when effectively masked.
  const birthdateDisplay = (() => {
    if (!detailFetched) return '​';
    if (effectivelyMasked) return '******';
    return isZeroDate(shopper?.dateOfBirth) ? '-' : display(shopper?.birthdate);
  })();

  const handleToggleReveal = () => {
    if (revealed) {
      setLocalHidden(prev => !prev);
    } else {
      setRevealModalOpen(true);
    }
  };

  const handleDownlineDetail = member => {
    const uplineRoute = ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id });
    const currentBreadcrumbs =
      breadcrumbsOverride ||
      [
        { name: 'Dashboard', href: ROUTES.DASHBOARD },
        { name: 'Shoppers', href: ROUTES.SHOPPERS },
        { name: fullName, href: uplineRoute },
      ];
    // Stack behavior: back should return to immediate previous page
    const nextBackToUpline = {
      label: 'Back to Upline',
      to: uplineRoute,
      state: {
        shopper,
        breadcrumbsOverride: currentBreadcrumbs,
        backToUpline: backToUpline || null,
      },
    };

    const shopperLabel =
      member.fullName ||
      [member.firstName, member.lastName].filter(Boolean).join(' ') ||
      member.id;
    const targetCrumbs = [
      ...currentBreadcrumbs,
      {
        name: shopperLabel,
        href: ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: member.id }),
      },
    ];
    navigate(ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: member.id }), {
      state: {
        shopper: member,
        backToUpline: nextBackToUpline,
        breadcrumbsOverride: targetCrumbs,
      },
    });
  };

  const handleOpenEarningsBonuses = () => {
    if (!shopper) return;
    navigate(
      ROUTES.generatePath(ROUTES.SHOPPERS_EARNINGS_BONUSES, {
        id: shopper.id,
      }),
      { state: { shopper } }
    );
  };

  const handleEditBank = () => {
    if (!shopper) return;
    navigate(
      ROUTES.generatePath(ROUTES.SHOPPERS_EDIT_BANK, { id: shopper.id }),
      { state: { shopper } }
    );
  };

  const handleEditPersonal = () => {
    if (!shopper) return;
    navigate(
      ROUTES.generatePath(ROUTES.SHOPPERS_EDIT_PERSONAL, { id: shopper.id }),
      { state: { shopper } }
    );
  };

  const handleEditCodiceFiscale = () => {
    if (!shopper) return;
    navigate(
      ROUTES.generatePath(ROUTES.SHOPPERS_EDIT_CODICE_FISCALE, {
        id: shopper.id,
      }),
      { state: { shopper } }
    );
  };

  const handleAdminActionSubmit = async ({ reason, password }) => {
    if (!shopper?.id) return;
    if (actionModal.type === 'delete') {
      await shopperService.deleteShopperAdmin(shopper.id, { reason, password });
      setActionSuccess('Shopper account has been immediately deleted.');
    } else {
      await shopperService.unblockShopperAdmin(shopper.id, { reason, password });
      setActionSuccess('Shopper account has been unblocked. 6-month re-registration block lifted.');
    }
    await fetchShopperDetails();
  };

  const isDeleted = shopper?.accountStatusCode === 'D';
  const deletedDate = shopper?.deleted ? new Date(shopper.deleted) : null;
  const cooldownUntil = deletedDate
    ? new Date(new Date(deletedDate).setMonth(deletedDate.getMonth() + 6))
    : null;
  const isCooldownActive = cooldownUntil ? new Date() < cooldownUntil : false;

  const fullName =
    shopper?.fullName ||
    [shopper?.firstName, shopper?.lastName].filter(Boolean).join(' ') ||
    'Shopper';

  const lastModifiedText =
    shopper?.lastModified && shopper.lastModified !== '-'
      ? `${shopper.lastModified}${
          shopper.modifiedByName ? ` (${shopper.modifiedByName})` : ''
        }`
      : '-';

  const linkedBanks = Array.isArray(shopper?.linkedBanks)
    ? shopper.linkedBanks
    : [];

  const downlineDisplayStart =
    downlinePagination.count > 0
      ? (downlinePagination.page - 1) * downlinePagination.size + 1
      : 0;
  const downlineDisplayEnd =
    downlinePagination.count > 0
      ? Math.min(
          downlinePagination.page * downlinePagination.size,
          downlinePagination.count
        )
      : 0;
  const downlinePageNumbers = Array.from(
    new Set(
      [
        1,
        downlinePagination.page - 1,
        downlinePagination.page,
        downlinePagination.page + 1,
        downlinePagination.pageCount,
      ].filter(page => page >= 1 && page <= downlinePagination.pageCount)
    )
  );

  const Field = ({ label, value, className = '', mono = false }) => (
    <div className={className}>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      <p
        className={`text-sm text-gray-900 break-words ${
          mono ? 'font-mono' : ''
        }`}
      >
        {display(value)}
      </p>
    </div>
  );

  const PencilIcon = ({ className = 'w-4 h-4' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
      />
    </svg>
  );

  const ExternalLinkIcon = ({ className = 'w-4 h-4' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
      />
    </svg>
  );

  const EyeIcon = ({ className = 'w-4 h-4' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
      />
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
      />
    </svg>
  );

  const EyeOffIcon = ({ className = 'w-4 h-4' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
      />
    </svg>
  );

  const ChevronIcon = ({ open }) => (
    <svg
      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
        open ? 'rotate-180' : ''
      }`}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
    </svg>
  );

  const SectionHeader = ({ title, action = 'edit', onAction }) => (
    <div className='mb-5'>
      <div className='flex items-center gap-2'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        {action && onAction && (
          <button
            type='button'
            onClick={onAction}
            className='inline-flex text-blue-600 hover:text-blue-800 p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1'
            aria-label={`${action === 'open' ? 'Open' : 'Edit'} ${title}`}
          >
            {action === 'open' ? <ExternalLinkIcon /> : <PencilIcon />}
          </button>
        )}
      </div>
      {/* TODO(BE-wiring): replace static placeholder with audit data */}
      {/* <p className='text-xs text-gray-500 mt-0.5'>
        Last Modified{' '}
        <span className='text-gray-700 font-medium'>{lastModifiedText}</span>
      </p> */}
    </div>
  );

  const StatusBadge = ({ label, tone = 'blue' }) => {
    const colorClass =
      tone === 'red'
        ? 'bg-red-100 text-red-700'
        : tone === 'green'
        ? 'bg-green-100 text-green-700'
        : tone === 'amber'
        ? 'bg-amber-100 text-amber-800'
        : label === 'Plus' || label === 'Active'
        ? 'bg-blue-100 text-blue-700'
        : label === 'Deleted'
        ? 'bg-red-100 text-red-700'
        : 'bg-gray-100 text-gray-700';

    return (
      <span
        className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${colorClass}`}
      >
        {label}
      </span>
    );
  };

  if (detailLoading && !shopper) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <p className='text-sm text-gray-600'>Loading shopper details...</p>
      </div>
    );
  }

  if (!shopper) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <div className='text-center'>
          <p className='text-sm text-red-600 mb-4'>
            {detailError || 'Shopper data not found.'}
          </p>
          <button
            type='button'
            onClick={() => navigate(ROUTES.SHOPPERS)}
            className='px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700'
          >
            Back to Shoppers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Shopper Details'
        subtitle='View detailed information about this shopper and their downline'
        breadcrumbs={
          breadcrumbsOverride || [
            { name: 'Dashboard', href: ROUTES.DASHBOARD },
            { name: 'Shoppers', href: ROUTES.SHOPPERS },
            { name: fullName },
          ]
        }
      />

      {detailError && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{detailError}</p>
        </div>
      )}

      {actionSuccess && (
        <div className='bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between'>
          <p className='text-sm text-green-800 font-medium'>{actionSuccess}</p>
          <button
            type='button'
            onClick={() => setActionSuccess(null)}
            className='text-green-600 hover:text-green-800 text-sm font-medium'
          >
            Dismiss
          </button>
        </div>
      )}

      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='flex items-start justify-between p-4 md:p-6 gap-4'>
          <div className='min-w-0'>
            <h2 className='text-xl font-bold text-gray-900 truncate'>
              {fullName}
            </h2>
            <p className='text-sm text-gray-500 mt-1 break-all'>
              Shopper ID: {display(shopper.id)}
            </p>
          </div>
          <div className='flex items-center gap-2 pt-1 flex-shrink-0'>
            {isDeleted ? (
              <>
                <StatusBadge label='Deleted' tone='red' />
                {shopper.unblockedAt && (
                  <StatusBadge label='Unblocked' tone='green' />
                )}
              </>
            ) : (
              <>
                <StatusBadge label={shopper.accountStatus || '-'} tone='green' />
                <StatusBadge label={shopper.status || '-'} />
              </>
            )}
            {detailFetched && (
              <button
                type='button'
                onClick={handleToggleReveal}
                className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md'
              >
                {effectivelyMasked ? <EyeIcon /> : <EyeOffIcon />}
                {effectivelyMasked ? 'Show' : 'Hide'}
              </button>
            )}
            {isDeleted && !shopper.unblockedAt && isCooldownActive && (
              <button
                type='button'
                onClick={() => setActionModal({ isOpen: true, type: 'unblock' })}
                className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z' />
                </svg>
                Unblock
              </button>
            )}
            {!isDeleted && (
              <button
                type='button'
                onClick={() => setActionModal({ isOpen: true, type: 'delete' })}
                className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                </svg>
                Delete
              </button>
            )}
            <button
              type='button'
              onClick={() => setIsCollapsed(prev => !prev)}
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              className='p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md'
            >
              <ChevronIcon open={!isCollapsed} />
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <div className='px-4 md:px-6 pb-6 space-y-8'>
            {isDeleted && (
              <div className='rounded-lg border border-red-200 bg-red-50/60 p-4'>
                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-full bg-red-100 text-red-600 flex-shrink-0'>
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                    </svg>
                  </div>
                  <div className='flex-1 min-w-0'>
                    <h4 className='text-sm font-semibold text-red-900'>
                      Deleted Shopper Account
                    </h4>
                    <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-xs'>
                      <div>
                        <span className='text-gray-500'>Deleted At:</span>{' '}
                        <span className='font-medium text-gray-800'>
                          {shopper.deleted ? new Date(shopper.deleted).toLocaleString() : '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>Deletion Reason:</span>{' '}
                        <span className='font-medium text-gray-800'>
                          {shopper.deletionReason || '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>6-Month Re-reg Block:</span>{' '}
                        {shopper.unblockedAt ? (
                          <span className='font-medium text-emerald-700'>
                            LIFTED (Unblocked)
                          </span>
                        ) : isCooldownActive ? (
                          <span className='font-medium text-red-700'>
                            ACTIVE until {cooldownUntil ? cooldownUntil.toLocaleDateString() : '-'}
                          </span>
                        ) : (
                          <span className='font-medium text-gray-600'>Expired</span>
                        )}
                      </div>
                      {shopper.unblockedAt && (
                        <>
                          <div>
                            <span className='text-gray-500'>Unblocked At:</span>{' '}
                            <span className='font-medium text-gray-800'>
                              {new Date(shopper.unblockedAt).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className='text-gray-500'>Unblocked By:</span>{' '}
                            <span className='font-medium text-gray-800'>
                              {shopper.unblockedByName || shopper.unblockedBy || '-'}
                            </span>
                          </div>
                          <div className='sm:col-span-2 lg:col-span-1'>
                            <span className='text-gray-500'>Unblock Reason:</span>{' '}
                            <span className='font-medium text-gray-800'>
                              {shopper.unblockReason || '-'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <section>
              <SectionHeader
                title='Personal Information'
                onAction={handleEditPersonal}
              />
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field label='First Name' value={shopper.firstName} />
                <Field label='Last Name' value={shopper.lastName} />
                <Field
                  label='Phone Number'
                  value={sensitiveValue(shopper.phone, maskPhone)}
                />
                <Field
                  label='Email Address'
                  value={sensitiveValue(shopper.email)}
                />
                <Field label='Birthdate' value={birthdateDisplay} />
                <Field label='Join Date' value={shopper.joinDate} />
              </div>
            </section>

            <div className='border-t border-gray-200' />

            <section>
              <SectionHeader title='Account & Subscriptions' action={null} />
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field label='Account Type' value={shopper.status} />
                <Field
                  label='Subscription Date'
                  value={shopper.subscriptionDate}
                />
                <Field
                  label='Subscription Due Date'
                  value={shopper.subscriptionDueDate}
                />
                <Field label='Account Status' value={shopper.accountStatus} />
              </div>
            </section>

            <div className='border-t border-gray-200' />

            <section>
              <SectionHeader
                title='Earnings & Bonuses'
                action='open'
                onAction={handleOpenEarningsBonuses}
              />
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field label='Cashback E&E' value={shopper.cashbackEne} />
                <Field
                  label='Cashback Merchant'
                  value={shopper.cashbackMerchant}
                />
                <Field
                  label='Withdrawal Balance'
                  value={shopper.withdrawalBalance}
                />
                <Field
                  label='Total Available Cashback'
                  value={shopper.availableCashback}
                />
              </div>
            </section>

            <div className='border-t border-gray-200' />

            <section>
              <SectionHeader title='Bank Account' action={null} />

              {/* Bank (sub-section) — IBAN + Beneficiary; edit hits its own page */}
              <div>
                <div className='bg-gray-50 px-4 py-2 mb-4 flex items-center gap-2'>
                  <h4 className='text-sm font-semibold text-gray-800'>Bank</h4>
                  <button
                    type='button'
                    onClick={handleEditBank}
                    aria-label='Edit Bank'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <PencilIcon />
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                  <Field
                    label='IBAN'
                    value={sensitiveValue(shopper.iban, maskTail)}
                  />
                  <Field
                    label='Beneficiary Name'
                    value={sensitiveValue(shopper.beneficiaryName)}
                  />
                </div>
              </div>

              <div className='mt-6'>
                <div className='bg-gray-50 px-4 py-2 mb-4 flex items-center gap-2'>
                  <h4 className='text-sm font-semibold text-gray-800'>
                    Codice Fiscale
                  </h4>
                  <button
                    type='button'
                    onClick={handleEditCodiceFiscale}
                    aria-label='Edit Codice Fiscale'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <PencilIcon />
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                  <Field
                    label='Codice Fiscale'
                    value={sensitiveValue(shopper.codiceFiscale, maskTail)}
                  />
                  <Field
                    label='Address'
                    value={sensitiveValue(shopper.codiceFiscaleAddress)}
                  />
                  <Field
                    label='Province'
                    value={shopper.codiceFiscaleProvince}
                  />
                  <Field label='City' value={shopper.codiceFiscaleCity} />
                  <Field
                    label='ZIP Code'
                    value={shopper.codiceFiscaleZipCode}
                  />
                </div>
              </div>
            </section>

            <div className='border-t border-gray-200' />

            <section>
              <SectionHeader title='Linked Bank' action={null} />
              <div className='space-y-5'>
                {linkedBanks.length === 0 && (
                  <p className='text-sm text-gray-500'>
                    No linked bank data available.
                  </p>
                )}
                {linkedBanks.map((bank, index) => (
                  <div
                    key={
                      bank.id ||
                      bank.accountId ||
                      bank.accountID ||
                      `${bank.institutionId || 'bank'}-${index}`
                    }
                  >
                    <div className='bg-gray-50 px-4 py-2 mb-4'>
                      <h4 className='text-sm font-semibold text-gray-800'>
                        {bank.institutionName ||
                          bank.institutionId ||
                          `Linked Bank ${index + 1}`}
                      </h4>
                    </div>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                      <Field
                        label='Institution ID'
                        value={bank.institutionId}
                      />
                      <Field
                        label='Account ID'
                        value={bank.accountId || bank.accountID}
                      />
                      <Field
                        label='Card Number'
                        value={sensitiveValue(bank.cardNumber, v =>
                          v ? (String(v).startsWith('**') ? v : `**${v}`) : '-'
                        )}
                      />
                      <Field
                        label='Name'
                        value={sensitiveValue(bank.name)}
                      />
                      <Field
                        label='Official Name'
                        value={sensitiveValue(bank.officialName)}
                      />
                      <Field
                        label='Account Type'
                        value={[bank.type, bank.subtype]
                          .filter(Boolean)
                          .join(' / ')}
                      />
                      <div>
                        <p className='text-xs text-gray-500 mb-1'>
                          Card Status
                        </p>
                        <p className='text-sm text-gray-900'>
                          {display(bank.status)}
                        </p>
                        {bank.note && (
                          <p className='text-xs text-red-600 mt-0.5'>
                            {bank.note}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className='border-t border-gray-200' />

            <section>
              <SectionHeader title='Network' action={null} />
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field label='Recommender' value={shopper.recommender} />
                <Field label='Direct Partner' value={shopper.directPartner} />
                <Field label='Total Downline' value={shopper.totalDownline} />
              </div>
            </section>
          </div>
        )}
      </div>

      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>Downline</h3>

          <div className='mt-4 border-b border-gray-200'>
            <nav
              className='flex -mb-px space-x-6'
              role='tablist'
              aria-label='Downline tabs'
            >
              <button
                type='button'
                className='border-blue-600 text-blue-600 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium'
              >
                Shopper
              </button>
            </nav>
          </div>

          {downlineError && (
            <div className='mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2'>
              <p className='text-sm text-red-700'>{downlineError}</p>
            </div>
          )}

          <p className='text-sm text-gray-500 mt-3'>
            Showing {downlineDisplayStart} to {downlineDisplayEnd} of{' '}
            {downlinePagination.count} downline members
          </p>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56'>
                  ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  First Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Join Date
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Referral Code
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Direct Partner
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Downline
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {downlineLoading && downline.length === 0 && (
                <tr>
                  <td colSpan={9} className='px-4 py-8 text-center text-sm text-gray-500'>
                    Loading downline...
                  </td>
                </tr>
              )}

              {!downlineLoading && downline.length === 0 && (
                <tr>
                  <td colSpan={9} className='px-4 py-8 text-center text-sm text-gray-500'>
                    No downline members found.
                  </td>
                </tr>
              )}

              {downline.map(member => {
                const isPlus = member.status === 'Plus';
                const referralValue = member.referralCode || member.referral || '-';

                return (
                  <tr key={member.id} className='hover:bg-gray-50 align-top'>
                    <td className='hidden lg:table-cell px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                      <div className='break-all whitespace-normal' title={member.id}>
                        {member.id}
                      </div>
                    </td>
                    <td className='px-4 py-4 align-top'>
                      <div className='text-sm font-medium text-gray-900'>
                        {member.firstName}
                      </div>
                    </td>
                    <td className='px-3 py-4 text-sm font-medium text-gray-900 align-top'>
                      {member.lastName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap align-top'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          isPlus
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.status || '-'}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 align-top'>
                      {member.joinDate || '-'}
                    </td>
                    <td className='px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                      <div
                        className='max-w-[180px] break-words'
                        title={referralValue}
                      >
                        {referralValue}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                      {member.partner ?? 0}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                      {member.downline ?? 0}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center align-top'>
                      <button
                        type='button'
                        onClick={() => handleDownlineDetail(member)}
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
          <p className='text-sm text-gray-700'>
            Page <span className='font-medium'>{downlinePagination.page}</span>{' '}
            of{' '}
            <span className='font-medium'>
              {downlinePagination.pageCount}
            </span>
          </p>
          <nav
            className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
            aria-label='Pagination'
          >
            <button
              type='button'
              onClick={() =>
                handleDownlinePageChange(downlinePagination.page - 1)
              }
              disabled={downlineLoading || downlinePagination.page <= 1}
              className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
            {downlinePageNumbers.map(num => (
              <button
                key={num}
                type='button'
                onClick={() => handleDownlinePageChange(num)}
                disabled={downlineLoading}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  num === downlinePagination.page
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {num}
              </button>
            ))}
            <button
              type='button'
              onClick={() =>
                handleDownlinePageChange(downlinePagination.page + 1)
              }
              disabled={
                downlineLoading ||
                downlinePagination.page >= downlinePagination.pageCount
              }
              className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </nav>
        </div>

        {backToUpline?.to && (
          <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-end'>
            <button
              type='button'
              onClick={() =>
                navigate(backToUpline.to, { state: backToUpline.state })
              }
              className='inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              {backToUpline.label || 'Back to Upline'}
            </button>
          </div>
        )}
      </div>

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => setRevealModalOpen(false)}
        onSuccess={() => fetchShopperDetails()}
      />

      <AdminAccountActionModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal(prev => ({ ...prev, isOpen: false }))}
        onSubmit={handleAdminActionSubmit}
        actionType={actionModal.type}
        accountType='Shopper'
        accountName={fullName}
      />
    </div>
  );
};

export default ShopperDetailPage;
